import React, { memo, useCallback } from 'react';
import { FileText, List, Tag, Users } from 'lucide-react';
import { useParams } from 'react-router-dom';
import { Flex } from 'rebass/styled-components';
import PropTypes from 'prop-types';

import { useSelector } from 'react-redux';
import { activeNetworkSelector } from 'redux/selectors';
import { useEditContactMutation } from '../../../services/contacts';
import { stripHtmlMarkdown } from '../../../helpers/stripHtmlMarkdown';
import { SearchImportersDropdown } from '../../molecules/searchImportersDropdown';
import { SearchSharedListsDropdown } from '../../molecules/searchSharedListsDropdown';
import { SearchTagsDropdown } from '../../molecules/searchTagsDropdown';
import { EditableAction } from './editableAction';
import { EditableActionItem } from './editableActionItem';
import { ContactMoreActions } from './contactMoreActions';
import AddContactsToList from '../contactCard/addContactToList';
import { useAddTagToSingleContacts } from './hooks/useAddTagToSingleContacts';
import { useAddListToSingleContacts } from './hooks/useAddListToSingleContacts';
import { RequestIntro } from './requestIntro';

export const ContactActionPanel = memo(
  ({
    type,
    page,
    contact,
    matchId,
    queryParams,
    actions,
    extendMethods,
    showDetailView,
    showMoreOptions,
    list: sharedList,
    deleteContactFromList,
    fullContactView,
    addedToSharedList,
  }) => {
    const [editContact] = useEditContactMutation();
    const activeNetwork = useSelector(activeNetworkSelector);
    const {
      onChangeTag,
      onCreateTag,
      isLoading: isAddingTagToContactsOrCreatingTag,
    } = useAddTagToSingleContacts({
      queryParams,
      contact,
      activeNetwork,
    });

    const {
      onChangeList,
      onCreateList,
      isLoading: isAddingListToContactsOrCreatingList,
    } = useAddListToSingleContacts({
      queryParams,
      contact,
      activeNetwork,
      origin: fullContactView ? 'full_profile_lists_management' : 'card_lists_management',
      page,
    });
    const params = useParams();

    const onChangeImporter = (importers) => {
      editContact({ queryParams, contactId: contact.id, values: { contactOf: importers } });
    };

    const onRemoveImporter = (importerOpt) => {
      onChangeImporter(contact.importers.filter((item) => item.id !== importerOpt.value));
    };

    const onRemoveList = (list) => onChangeList([], { value: list.id }, false);

    const onRemoveTag = (opt) => onChangeTag([], opt, false);

    const formatNoteLabel = useCallback((item) => {
      const nameTokens = item.author.fullName.split(' ');
      const author = `${nameTokens[0]}${nameTokens[1] ? ` ${Array.from(nameTokens[1])[0]}` : ''}.`;
      return `${author}: ${item.title ? item.title : ''} ${stripHtmlMarkdown(item.body)}`;
    }, []);

    const actionList = [
      {
        id: 'add-importer',
        icon: Users,
        label: 'Contact of',
        items: contact?.importers?.map((item) => ({ label: item.name, value: item.id })) ?? [],
        DropdownComponent: SearchImportersDropdown,
        EditableActionItemComponent: EditableActionItem,
        onChange: onChangeImporter,
        onRemove: onRemoveImporter,
        ...(extendMethods?.['add-importer'] ? { extendMethods: extendMethods['add-importer'] } : undefined),
      },
      {
        id: 'show-importers',
        icon: Users,
        label: 'Contact of',
        items: contact?.importers?.map((item) => ({ label: item.name, value: item.id })) ?? [],
        EditableActionItemComponent: EditableActionItem,
        isPublic: type === 'public',
      },
      {
        id: 'lists',
        icon: List,
        label: 'Lists',
        items: contact?.sharedList?.map((item) => ({ id: item.id, label: item.name, value: item.id })) || [],
        DropdownComponent: SearchSharedListsDropdown,
        EditableActionItemComponent: EditableActionItem,
        onChange: onChangeList,
        onRemove: onRemoveList,
        isLoading: isAddingListToContactsOrCreatingList,
        onCreate: onCreateList,
        ...(extendMethods?.lists ? { extendMethods: extendMethods.lists } : undefined),
      },
      {
        id: 'tags',
        icon: Tag,
        label: 'Tags',
        items: contact?.tags?.map((item) => ({ id: item.id, label: item.tag, value: item.id })) || [],
        DropdownComponent: SearchTagsDropdown,
        EditableActionItemComponent: EditableActionItem,
        onChange: onChangeTag,
        onRemove: onRemoveTag,
        onCreate: onCreateTag,
        isLoading: isAddingTagToContactsOrCreatingTag,
        ...(extendMethods?.tags ? { extendMethods: extendMethods.tags } : undefined),
      },
      {
        id: 'notes',
        icon: FileText,
        label: 'Notes',
        items: (contact.notes || []).map((item) => ({
          id: item.id,
          label: formatNoteLabel(item),
          value: item.id,
        })),
        EditableActionItemComponent: EditableActionItem,
        showDetailView,
        ...(extendMethods?.notes ? { extendMethods: extendMethods.notes } : undefined),
      },
    ];

    return (
      <Flex sx={{ gap: '16px', flexDirection: 'column' }}>
        {sharedList?.introRequestsEnabled && type === 'public' && (
          <RequestIntro list={sharedList} selectedContacts={[contact]} slug={params.slug} />
        )}
        {['default', 'matches'].includes(type) && sharedList?.id && !deleteContactFromList && (
          <AddContactsToList
            page={page}
            addedToSharedList={addedToSharedList}
            fullContactView={fullContactView}
            queryParams={queryParams}
            contact={contact}
            matchId={matchId}
            list={sharedList}
          />
        )}
        <Flex justifyContent="space-between" sx={{ position: 'relative' }}>
          <Flex flexDirection="column" sx={{ gap: '16px' }}>
            {actions.map((actionId) => {
              const action = actionList.find((item) => item.id === actionId);

              return action ? <EditableAction key={`panel-action-${action.id}`} action={action} /> : null;
            })}
          </Flex>
          {!['public', 'matches'].includes(type) && (
            <ContactMoreActions
              page={page}
              queryParams={queryParams}
              showMoreOptions={showMoreOptions}
              contact={contact}
              deleteContactFromList={deleteContactFromList}
              list={sharedList}
              fullContactView={fullContactView}
            />
          )}
        </Flex>
      </Flex>
    );
  },
);

ContactActionPanel.propTypes = {
  type: PropTypes.oneOf(['default', 'read', 'matches', 'public']),
  page: PropTypes.string,
  actions: PropTypes.array,
  queryParams: PropTypes.object,
  contact: PropTypes.object.isRequired,
  matchId: PropTypes.number,
  extendMethods: PropTypes.object,
  showDetailView: PropTypes.func,
  showMoreOptions: PropTypes.bool,
  list: PropTypes.object,
  deleteContactFromList: PropTypes.bool,
  fullContactView: PropTypes.bool,
  addedToSharedList: PropTypes.bool,
};

ContactActionPanel.defaultProps = {
  type: 'default',
  page: '',
  actions: ['add-importer', 'lists', 'tags', 'notes'],
  extendMethods: null,
  queryParams: {},
  matchId: null,
  showDetailView: null,
  showMoreOptions: false,
  list: null,
  deleteContactFromList: false,
  fullContactView: false,
  addedToSharedList: false,
};
